import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Clock } from "lucide-react";
import useLang from "../../hooks/useLang";

const ProjectCard = ({ project, index = 0 }) => {
  const { t, isRTL } = useLang();
  const isPending = project.status === "pending";
  const title = isRTL ? project.titleAr : project.title;
  const description =
    (isRTL ? project.descriptionAr : project.description) ||
    (isPending ? t("ourWork.pendingNote") : "");
  const techStack = project.techStack || [];

  const categoryColors = {
    Web: "bg-blue-50 text-blue-700",
    Mobile: "bg-purple-50 text-purple-700",
    AI: "bg-emerald-50 text-emerald-700",
    SaaS: "bg-amber-50 text-amber-700",
    ERP: "bg-rose-50 text-rose-700",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300"
    >
      <div className="relative overflow-hidden h-48">
        {project.image ? (
          <img
            src={project.image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary to-dark flex items-center justify-center">
            <Clock size={36} className="text-accent/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {project.category && (
          <span
            className={`absolute top-3 ${isRTL ? "right-3" : "left-3"} text-xs font-semibold px-2.5 py-1 rounded-full ${
              categoryColors[project.category] || "bg-gray-100 text-gray-700"
            }`}
          >
            {project.category}
          </span>
        )}
        {isPending && (
          <span
            className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700`}
          >
            {t("ourWork.pending")}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 text-base mb-2 line-clamp-1">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{description}</p>
        {techStack.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {techStack.map((tech) => (
              <span
                key={tech}
                className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full border border-gray-100"
              >
                {tech}
              </span>
            ))}
          </div>
        )}
        {!isPending && (
          <a
            href={project.link}
            className="inline-flex items-center gap-1.5 text-accent text-sm font-medium hover:gap-2.5 transition-all"
          >
            {t("ourWork.projectCta")} <ExternalLink size={13} />
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default ProjectCard;
